/**
 * GridState — the single object flowing through grid simulation graphs.
 *
 * A GridState is a set of named 2D layers on a cols×rows grid.
 * System functions take a GridState and return a new GridState.
 * Unmodified layers share references (structural sharing).
 */

// ── GridState ──

export interface GridState {
  readonly cols: number;
  readonly rows: number;
  readonly layers: Record<string, number[][]>;
}

/** Create a GridState with named layers. */
export function createGridState(
  cols: number,
  rows: number,
  layers: Record<string, number[][]>,
): GridState {
  return { cols, rows, layers };
}

/** Deep-clone a GridState. Only clones specified layers; others share refs. */
export function cloneGridState(state: GridState, cloneLayers?: string[]): GridState {
  const layers: Record<string, number[][]> = { ...state.layers };
  const toClone = cloneLayers ?? Object.keys(layers);
  for (const name of toClone) {
    const layer = layers[name];
    if (layer) {
      layers[name] = layer.map(row => row.slice());
    }
  }
  return { cols: state.cols, rows: state.rows, layers };
}

/** Create an empty (zero-filled or custom-filled) layer. */
export function emptyLayer(cols: number, rows: number, fill = 0): number[][] {
  const layer: number[][] = [];
  for (let r = 0; r < rows; r++) {
    const row = new Array<number>(cols);
    row.fill(fill);
    layer.push(row);
  }
  return layer;
}

/** Get a layer by name, throw if missing. */
export function getLayer(state: GridState, name: string): number[][] {
  const layer = state.layers[name];
  if (!layer) throw new Error(`GridState: layer "${name}" does not exist`);
  return layer;
}

/** Iterate every cell position. */
export function forEachCell(
  cols: number,
  rows: number,
  fn: (col: number, row: number) => void,
): void {
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      fn(c, r);
    }
  }
}

// ── Neighbour helpers (operate on raw number[][]) ──

/** Von Neumann neighbourhood (4 cardinal directions). */
export const VON_NEUMANN: ReadonlyArray<readonly [number, number]> = [
         [0, -1],
  [-1, 0],        [1, 0],
         [0,  1],
];

/** Moore neighbourhood (8 surrounding cells). */
export const MOORE: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [0, -1], [1, -1],
  [-1,  0],          [1,  0],
  [-1,  1], [0,  1], [1,  1],
];

/** Get values of neighbours in a layer, with optional wrapping. */
export function getNeighbourValues(
  layer: number[][],
  cols: number,
  rows: number,
  col: number,
  row: number,
  offsets: ReadonlyArray<readonly [number, number]>,
  wrap: boolean,
): number[] {
  const result: number[] = [];
  for (const [dc, dr] of offsets) {
    let c = col + dc;
    let r = row + dr;
    if (wrap) {
      c = ((c % cols) + cols) % cols;
      r = ((r % rows) + rows) % rows;
    } else if (c < 0 || c >= cols || r < 0 || r >= rows) {
      continue;
    }
    result.push(layer[r][c]);
  }
  return result;
}

/** Sum neighbour values. */
export function sumNeighbours(
  layer: number[][],
  cols: number,
  rows: number,
  col: number,
  row: number,
  offsets: ReadonlyArray<readonly [number, number]>,
  wrap: boolean,
): number {
  let sum = 0;
  for (const [dc, dr] of offsets) {
    let c = col + dc;
    let r = row + dr;
    if (wrap) {
      c = ((c % cols) + cols) % cols;
      r = ((r % rows) + rows) % rows;
    } else if (c < 0 || c >= cols || r < 0 || r >= rows) {
      continue;
    }
    sum += layer[r][c];
  }
  return sum;
}

/** Count neighbours above a threshold. */
export function countNeighboursAbove(
  layer: number[][],
  cols: number,
  rows: number,
  col: number,
  row: number,
  offsets: ReadonlyArray<readonly [number, number]>,
  wrap: boolean,
  threshold = 0,
): number {
  let count = 0;
  for (const [dc, dr] of offsets) {
    let c = col + dc;
    let r = row + dr;
    if (wrap) {
      c = ((c % cols) + cols) % cols;
      r = ((r % rows) + rows) % rows;
    } else if (c < 0 || c >= cols || r < 0 || r >= rows) {
      continue;
    }
    if (layer[r][c] > threshold) count++;
  }
  return count;
}

// ── Seeded RNG ──

/** Linear congruential generator. Deterministic given the same seed. */
export function makeRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

// ── Noise ──

function noiseHash(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + seed * 113.5) * 43758.5453;
  return n - Math.floor(n);
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const u = smoothstep(x - ix);
  const v = smoothstep(y - iy);
  const a = noiseHash(ix, iy, seed);
  const b = noiseHash(ix + 1, iy, seed);
  const c = noiseHash(ix, iy + 1, seed);
  const d = noiseHash(ix + 1, iy + 1, seed);
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v;
}

/**
 * Fractal Brownian Motion noise. Returns values centered around 0 (range ~ -0.5 to 0.5).
 */
export function fbm(
  x: number, y: number, seed: number,
  octaves = 4, persistence = 0.5, lacunarity = 2,
): number {
  let total = 0, amplitude = 1, frequency = 1, maxValue = 0;
  for (let i = 0; i < octaves; i++) {
    total += valueNoise(x * frequency, y * frequency, seed + i * 100) * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }
  return (total / maxValue) - 0.5;
}
